import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
// Browser-safe subpath: the core index pulls in node:crypto via ids.ts.
import { COMPANY } from "@app/core/company";
import { useLang } from "../i18n.js";
import type { Lang } from "../i18n.js";
import { BrandMark } from "../components/BrandMark.js";
import { useSeo } from "../seo.js";
import { HOME_SEO } from "../home-seo.js";
import { MiniIcon } from "../landing/illustrations.js";
import type { MiniIconName } from "../landing/illustrations.js";
import { BrowserMock, LcaBentoCard, PassportBentoCard, TerminalMock } from "../landing/mockups.js";
import { BlogCover, FlowMedia, HeroVideoBg } from "../landing/media.js";
import "../landing/landing.css";

/**
 * Public marketing landing, Vercel-home style: an SMIL-animated SVG emblem in
 * the hero, big thin headlines, product mockups (browser + terminal) with
 * monospace feature lists, and bento cards. No WebGL, no images — everything
 * is CSS/SVG. Marketing copy lives here as a local tr/en content object.
 */

interface Copy {
  heroKicker: string;
  h1: string;
  /** Second half of the hero headline, rendered in the accent gradient. */
  h1Em: string;
  lead: string;
  ctaLogin: string;
  ctaHow: string;
  heroList: string[];
  heroStandards: string[];
  bandCaption: string;
  featuresLabel: string;
  show1Title: string;
  show1AsideA: string;
  show1AsideB: string;
  show1Features: string[];
  show2Title: string;
  show2AsideA: string;
  show2AsideB: string;
  show2Features: string[];
  whyTitle: string;
  whySub: string;
  timeline: { b: string; s: string }[];
  insideTitle: string;
  insideSub: string;
  bentoFlowTitle: string;
  bentoFlowText: string;
  features: { i: MiniIconName; h: string; p: string }[];
  diffTitle: string;
  diffSub: string;
  diffs: { i: MiniIconName; h: string; p: string }[];
  diffCtaTitle: string;
  diffCtaText: string;
  blogTitle: string;
  blogSub: string;
  blogAll: string;
  ctaTitle: string;
  ctaText: string;
  ctaBtn: string;
  footer: string;
  footerAbout: string;
  footerGeneralTitle: string;
  footerGeneral: { label: string; href: string }[];
  footerResourcesTitle: string;
  footerResources: { label: string; href: string }[];
  footerRights: string;
  footerContactTitle: string;
  footerHq: string;
  footerPhone: string;
  footerTax: string;
  footerMersis: string;
}

const COPY: Record<"tr" | "en", Copy> = {
  tr: {
    heroKicker: "DİJİTAL ÜRÜN PASAPORTU PLATFORMU",
    h1: "Dijital Ürün",
    h1Em: "Pasaportu",
    lead:
      "AB'ye ihracat için uçtan uca DPP platformu: ürün verinizi bir kez girin — " +
      "her şey QR ile açılan imzalı bir pasaport sayfasında.",
    ctaLogin: "Panele giriş",
    ctaHow: "Nasıl çalışır?",
    heroList: ["İHRACATÇI KOBİ'LER İÇİN", "ESPR & EN 1821x UYUMLU", "LCA MOTORU İÇİNDE"],
    heroStandards: ["ESPR 2024/1781", "EN 18219", "EN 18220", "EN 18222", "GS1 DIGITAL LINK", "JSON-LD"],
    bandCaption: "ÜRÜN → QR → İMZALI PASAPORT",
    featuresLabel: "Özellikler",
    show1Title: "Tek QR — herkese kendi görünümü",
    show1AsideA: "Public pasaport sayfası",
    show1AsideB: "telefonda 1.5 saniyede açılır; herkes yalnızca rolüne açılan veriyi görür.",
    show1Features: [
      "KATMANLI ERİŞİM (EN 18239)",
      "BAKIM SEMBOLLERİ",
      "TEDARİK ZİNCİRİ HARİTASI",
      "BİLEŞEN İZLENEBİLİRLİĞİ",
    ],
    show2Title: "Yayım tek tık — gerisi otomatik",
    show2AsideA: "Yayım akışı",
    show2AsideB: "uyum kontrolü, imza, sürüm, QR ve Registry kaydını tek adımda tamamlar.",
    show2Features: ["GS1 DIGITAL LINK", "ED25519 İMZALI SÜRÜMLER", "EU DPP REGISTRY", "BASKIYA HAZIR QR"],
    whyTitle: "Neden şimdi?",
    whySub: "Takvim işliyor — satın alma kararları zorunluluktan önce veriliyor.",
    timeline: [
      { b: "Temmuz 2026 — ESPR yürürlükte", s: "AB Merkezi DPP Registry canlı." },
      { b: "Mayıs 2026 — EN 1821x yayımlandı", s: "Neye göre inşa edileceği artık net." },
      { b: "Şubat 2027 — Batarya pasaportu zorunlu", s: "İlk yasal DPP tarihi." },
      { b: "~2027 — Tekstil delegated act", s: "Fiili zorunluluk ~2028-29." },
      { b: "Bugün — Alıcı markalar veri istiyor", s: "Şartnamelere DPP maddeleri giriyor." },
    ],
    insideTitle: "Pasaportun içinde ne var?",
    insideSub: "Her bölüm regülasyonun bir gereksinimini karşılar.",
    bentoFlowTitle: "Üründen role: veri akışı",
    bentoFlowText: "Ürün → QR → pasaport; tüketici, gümrük ve geri dönüşümcü kendi katmanını görür.",
    features: [
      { i: "layers", h: "Malzeme kompozisyonu", p: "Katman bazlı elyaf, geri dönüştürülmüş oran, menşe." },
      { i: "droplet", h: "Bakım sembolleri", p: "İkon tabanlı, üç dilde otomatik." },
      { i: "pin", h: "Tedarik zinciri + harita", p: "9 aşama, tesis kartları, konum haritası." },
      { i: "branch", h: "Bileşen izlenebilirliği", p: "İplikten etikete parça parça kaynak." },
      { i: "bars", h: "LCA — metodolojisiyle", p: "CO₂e, su, enerji + metot ve veri seti beyanı." },
      { i: "loop", h: "Döngüsellik + partnerler", p: "Onarım, bağış, geri dönüşüm, ikinci el." },
      { i: "rosette", h: "Sertifikalar", p: "GOTS, OEKO-TEX — geçerlilik takibiyle." },
      { i: "star", h: "Müşteri yorumları", p: "Moderasyonlu, KVKK'ya uygun." },
      { i: "lock", h: "Katmanlı erişim", p: "Kamu / tamirci / geri dönüşümcü / gümrük." },
      { i: "pen", h: "İmzalı sürümler", p: "Hash zincirli, değişmez, doğrulanabilir." },
      { i: "globe", h: "Çok dilli public sayfa", p: "TR · EN · DE, uygulamasız ve hızlı." },
      { i: "braces", h: "GS1 Digital Link + QR", p: "Baskıya hazır QR, Data Matrix, NFC." },
    ],
    diffTitle: "Bizi farklı kılan",
    diffSub: '"DPP yazılımı" değil: LCA motoru olan DPP platformu.',
    diffs: [
      { i: "flask", h: "LCA motoru içeride", p: "clca.app'ten ecoinvent tabanlı gerçek sonuç." },
      { i: "globe", h: "Türkçe tedarik zinciri", p: "Veriyi gerçekten toplayan Türkçe portal." },
      { i: "tag", h: "KOBİ fiyatı, şeffaf", p: "İhracatçının ödeyebileceği açık planlar." },
    ],
    diffCtaTitle: "Farkı kendiniz görün",
    diffCtaText: "Bir günde standart-uyumlu bir pasaport yayımlayın.",
    blogTitle: "Blogdan son yazılar",
    blogSub: "DPP, ESPR ve AB'ye ihracat uyumu üzerine rehberler.",
    blogAll: "Tüm yazılar",
    ctaTitle: "Pasaportunuzu bugün yayımlayın",
    ctaText: "Standart-uyumlu bir tekstil pasaportunu bir günde üretin.",
    ctaBtn: "Panele giriş",
    footer: "ESPR (EU) 2024/1781 · EN 18219/18220/18221/18222 · GS1 Digital Link · KVKK/GDPR",
    footerAbout:
      "AB'ye ihracat yapan KOBİ'ler için uçtan uca Dijital Ürün Pasaportu platformu. ESPR ve " +
      "EN 1821x uyumlu — ürün verinizi bir kez girin, QR ile açılan imzalı bir pasaportta yayımlayın.",
    footerGeneralTitle: "Genel",
    footerGeneral: [
      { label: "Ana sayfa", href: "/" },
      { label: "Nasıl çalışır?", href: "#passport" },
      { label: "Özellikler", href: "#inside" },
      { label: "Blog", href: "/blog" },
      { label: "Rehberler", href: "/rehber" },
      { label: "Panele giriş", href: "/login" },
    ],
    footerResourcesTitle: "Kaynaklar",
    footerResources: [
      { label: "Dijital Ürün Pasaportu rehberi", href: "/rehber/dijital-urun-pasaportu-rehberi" },
      { label: "ESPR rehberi", href: "/rehber/espr-rehberi" },
      { label: "DPP sözlüğü", href: "/rehber/dpp-sozlugu" },
      { label: "Tekstil DPP rehberi", href: "/rehber/tekstil-dpp-rehberi" },
      { label: "Batarya pasaportu rehberi", href: "/rehber/batarya-pasaportu-rehberi" },
      { label: "Tüm rehberler", href: "/rehber" },
    ],
    footerRights: "© 2026 IDPP.net — Tüm hakları saklıdır.",
    footerContactTitle: "İletişim",
    footerHq: "Merkez",
    footerPhone: "Tel",
    footerTax: `VD: ${COMPANY.taxOffice} - VN: ${COMPANY.taxNumber}`,
    footerMersis: `Mersis No: ${COMPANY.mersisNo}`,
  },
  en: {
    heroKicker: "THE DIGITAL PRODUCT PASSPORT PLATFORM",
    h1: "Digital Product",
    h1Em: "Passport",
    lead:
      "The end-to-end DPP platform for EU exports: enter your product data once — " +
      "everything lives on a signed passport page behind a QR code.",
    ctaLogin: "Sign in",
    ctaHow: "How it works",
    heroList: ["FOR EXPORTING SMEs", "ESPR & EN 1821x ALIGNED", "LCA ENGINE INSIDE"],
    heroStandards: ["ESPR 2024/1781", "EN 18219", "EN 18220", "EN 18222", "GS1 DIGITAL LINK", "JSON-LD"],
    bandCaption: "PRODUCT → QR → SIGNED PASSPORT",
    featuresLabel: "Features",
    show1Title: "One QR — every role its own view",
    show1AsideA: "The public passport page",
    show1AsideB: "loads on a phone in 1.5 seconds; each role sees only the data opened to it.",
    show1Features: [
      "LAYERED ACCESS (EN 18239)",
      "CARE SYMBOLS",
      "SUPPLY CHAIN MAP",
      "COMPONENT TRACEABILITY",
    ],
    show2Title: "Publish once — the rest is automatic",
    show2AsideA: "The publish flow",
    show2AsideB:
      "completes compliance checks, signing, versioning, QR and Registry registration in one step.",
    show2Features: ["GS1 DIGITAL LINK", "ED25519 SIGNED VERSIONS", "EU DPP REGISTRY", "PRINT-READY QR"],
    whyTitle: "Why now?",
    whySub: "The clock is running — buying decisions precede the mandate.",
    timeline: [
      { b: "July 2026 — ESPR applicable", s: "The central EU DPP Registry is live." },
      { b: "May 2026 — EN 1821x published", s: "What to build against is now clear." },
      { b: "February 2027 — Battery passport mandatory", s: "The first fixed legal DPP date." },
      { b: "~2027 — Textile delegated act", s: "De-facto mandatory ~2028-29." },
      { b: "Today — Buyer brands ask for data", s: "DPP clauses are entering tenders." },
    ],
    insideTitle: "What's inside the passport?",
    insideSub: "Every section answers a regulatory requirement.",
    bentoFlowTitle: "From product to role: the data flow",
    bentoFlowText: "Product → QR → passport; consumer, customs and recycler each see their own layer.",
    features: [
      { i: "layers", h: "Material composition", p: "Layer-level fibers, recycled share, origin." },
      { i: "droplet", h: "Care symbols", p: "Icon-based, automatic in three languages." },
      { i: "pin", h: "Supply chain + map", p: "Nine stages, facility cards, location map." },
      { i: "branch", h: "Component traceability", p: "Source of every part, thread to label." },
      { i: "bars", h: "LCA — with methodology", p: "CO₂e, water, energy + method and dataset." },
      { i: "loop", h: "Circularity + partners", p: "Repair, donation, recycling, resale." },
      { i: "rosette", h: "Certifications", p: "GOTS, OEKO-TEX — with validity tracking." },
      { i: "star", h: "Customer feedback", p: "Moderated, GDPR-lean." },
      { i: "lock", h: "Layered access", p: "Public / repairer / recycler / customs." },
      { i: "pen", h: "Signed versions", p: "Hash-chained, immutable, verifiable." },
      { i: "globe", h: "Multilingual public page", p: "TR · EN · DE, app-free and fast." },
      { i: "braces", h: "GS1 Digital Link + QR", p: "Print-ready QR, Data Matrix, NFC." },
    ],
    diffTitle: "What sets us apart",
    diffSub: 'Not "DPP software": a DPP platform with an LCA engine inside.',
    diffs: [
      { i: "flask", h: "LCA engine built in", p: "Real ecoinvent-based results from clca.app." },
      { i: "globe", h: "Turkish supply chain", p: "A portal that actually collects the data." },
      { i: "tag", h: "SME pricing, transparent", p: "Open plans an exporter can afford." },
    ],
    diffCtaTitle: "See the difference",
    diffCtaText: "Publish a standards-compliant passport in a day.",
    blogTitle: "Latest from the blog",
    blogSub: "Guides on the DPP, ESPR and EU export compliance.",
    blogAll: "All posts",
    ctaTitle: "Publish your passport today",
    ctaText: "Produce a standards-compliant textile passport in a day.",
    ctaBtn: "Sign in",
    footer: "ESPR (EU) 2024/1781 · EN 18219/18220/18221/18222 · GS1 Digital Link · GDPR",
    footerAbout:
      "The end-to-end Digital Product Passport platform for SMEs exporting to the EU. ESPR & " +
      "EN 1821x aligned — enter your product data once, publish it on a signed passport behind a QR code.",
    footerGeneralTitle: "General",
    footerGeneral: [
      { label: "Home", href: "/" },
      { label: "How it works", href: "#passport" },
      { label: "Features", href: "#inside" },
      { label: "Blog", href: "/en/blog" },
      { label: "Guides", href: "/en/guides" },
      { label: "Sign in", href: "/login" },
    ],
    footerResourcesTitle: "Resources",
    footerResources: [
      { label: "Digital Product Passport guide", href: "/en/guides/digital-product-passport-guide" },
      { label: "ESPR guide", href: "/en/guides/espr-guide" },
      { label: "DPP glossary", href: "/en/guides/dpp-glossary" },
      { label: "Textile DPP guide", href: "/en/guides/textile-dpp-guide" },
      { label: "Battery passport guide", href: "/en/guides/battery-passport-guide" },
      { label: "All guides", href: "/en/guides" },
    ],
    footerRights: "© 2026 IDPP.net — All rights reserved.",
    footerContactTitle: "Contact",
    footerHq: "Head office",
    footerPhone: "Phone",
    footerTax: `Tax office: ${COMPANY.taxOffice} · Tax no: ${COMPANY.taxNumber}`,
    footerMersis: `MERSİS no: ${COMPANY.mersisNo}`,
  },
};

/**
 * The blog and the guide pages are SERVER-RENDERED by the API (see
 * apps/api/src/routes/blog.routes.ts) — they are not SPA routes, so they must be
 * reached with a plain anchor, never react-router's <Link>.
 */
function isSpaRoute(href: string): boolean {
  return href === "/" || href === "/login" || !href.startsWith("/");
}

function blogHref(lang: Lang): string {
  return lang === "tr" ? "/blog" : "/en/blog";
}

interface TeaserPost {
  key: string;
  /** Server-rendered post URL for this language (`/blog/<slug>` | `/en/blog/<slug>`). */
  url: string;
  title: string;
  excerpt: string;
  tags?: string[];
}

/**
 * Sun/moon glyph for the landing theme toggle. Following the app convention,
 * we show the icon of the mode you'd switch TO: a moon while light, a sun while
 * dark. Pure inline SVG so the marketing page stays free of app chrome.
 */
function ThemeIcon({ theme }: { theme: "dark" | "light" }) {
  const common = {
    width: 16,
    height: 16,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  return theme === "light" ? (
    <svg {...common}>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
    </svg>
  ) : (
    <svg {...common}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

/**
 * Landing "latest from the blog" section — pulls the 3 newest posts from the
 * auth-free blog JSON API. Renders nothing if the API is unavailable, so a blog
 * hiccup never breaks the marketing page.
 */
function BlogSection({ lang, title, sub, all }: { lang: Lang; title: string; sub: string; all: string }) {
  const [posts, setPosts] = useState<TeaserPost[]>([]);
  useEffect(() => {
    let alive = true;
    fetch(`/blog-api/posts?lang=${lang}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d: { posts: TeaserPost[] }) => alive && setPosts(d.posts.slice(0, 3)))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [lang]);

  if (posts.length === 0) return null;

  return (
    <section className="ld-section" id="blog">
      <span className="ld-idx">06</span>
      <h2>{title}</h2>
      <p className="ld-sub">{sub}</p>
      <div className="ld-grid ld-blog-grid ld-grid-4">
        {posts.map((p) => (
          <a className="ld-feature ld-blog-card" href={p.url} key={p.key}>
            <BlogCover postKey={p.key} kicker={p.tags?.[0]?.replace(/-/g, " ").toUpperCase()} />
            <div className="ld-blog-card-body">
              <h3>{p.title}</h3>
              <p>{p.excerpt}</p>
            </div>
          </a>
        ))}
        <a className="ld-feature ld-blog-card ld-blog-all-card" href={blogHref(lang)}>
          <div className="ld-blog-all-inner">
            <span className="ld-blog-all-arrow" aria-hidden="true">
              →
            </span>
            <h3>{all}</h3>
            <p>{sub}</p>
          </div>
        </a>
      </div>
    </section>
  );
}

export function LandingPage({ forceLang }: { forceLang?: Lang } = {}) {
  const { lang, setLang } = useLang();
  const c = COPY[lang];

  // `/en` is the English home page (the hreflang counterpart of `/`), so landing
  // there pins the app language instead of showing the remembered one.
  useEffect(() => {
    if (forceLang && forceLang !== lang) setLang(forceLang);
  }, [forceLang, lang, setLang]);

  // Copy lives in home-seo.ts, shared with the build-time /en head variant.
  const seo = HOME_SEO[lang];
  useSeo({
    title: seo.title,
    description: seo.description,
    path: seo.path,
    altPath: HOME_SEO[lang === "tr" ? "en" : "tr"].path,
    lang,
  });

  // Landing-local theme. The public marketing surface keeps its own dark/light
  // switch (defaulting to the cinematic dark) independent of the in-app
  // ThemeProvider, persisted so a visitor's choice sticks across reloads.
  const [ldTheme, setLdTheme] = useState<"dark" | "light">(() =>
    typeof window !== "undefined" && localStorage.getItem("idpp-ld-theme") === "light" ? "light" : "dark",
  );
  useEffect(() => {
    if (typeof window !== "undefined") localStorage.setItem("idpp-ld-theme", ldTheme);
  }, [ldTheme]);

  // The topbar floats transparently over the cinematic hero so the video reads
  // edge-to-edge from the top of the page; it only takes on its blurred chrome
  // once the visitor has scrolled past the hero's first band.
  const [barStuck, setBarStuck] = useState(false);
  useEffect(() => {
    const onScroll = () => setBarStuck(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="ld-page" data-ld-theme={ldTheme}>
      <div className="ld-container">
        <header className={`ld-topbar ld-topbar-float${barStuck ? " is-stuck" : ""}`}>
          <div className="ld-logo">
            <BrandMark size={20} className="brand-mark" />
            IDPP<span>.net</span>
          </div>
          <nav className="ld-nav">
            <a className="ld-btn ld-btn-ghost" href={blogHref(lang)}>
              Blog
            </a>
            <a
              className="ld-btn ld-btn-ghost"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setLang(lang === "tr" ? "en" : "tr");
              }}
            >
              {lang === "tr" ? "EN" : "TR"}
            </a>
            <button
              type="button"
              className="ld-theme-toggle"
              aria-label={lang === "tr" ? "Temayı değiştir" : "Toggle theme"}
              onClick={() => setLdTheme((t) => (t === "dark" ? "light" : "dark"))}
            >
              <ThemeIcon theme={ldTheme} />
            </button>
            <Link className="ld-btn ld-btn-primary" to="/login">
              {c.ctaLogin}
            </Link>
          </nav>
        </header>

        {/* Full-bleed cinematic hero: the clip fills the viewport width and runs
            up behind the floating topbar; the copy sits in `ld-herocine-inner`,
            which repeats the page's content column so the headline lines up with
            the logo, the standards strip and every section below it. */}
        <section className="ld-hero-v ld-herocine">
          <HeroVideoBg />
          <div className="ld-herocine-inner">
            <div className="ld-herocine-copy">
              <span className="ld-kicker">{c.heroKicker}</span>
              <h1>
                {c.h1} <em>{c.h1Em}</em>
              </h1>
              <p className="ld-lead">{c.lead}</p>
              <div className="ld-cta-row">
                <Link className="ld-btn ld-btn-primary" to="/login">
                  {c.ctaLogin}
                </Link>
                <a className="ld-btn ld-btn-ghost" href="#passport">
                  {c.ctaHow}
                </a>
              </div>
              <ul className="ld-hero-list">
                {c.heroList.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>
        <div className="ld-logos">
          {c.heroStandards.map((s) => (
            <span key={s}>{s}</span>
          ))}
        </div>

        {/* Showcase 01 — the public page, browser mockup left. */}
        <section className="ld-section no-top ld-show" id="passport">
          <span className="ld-idx">01</span>
          <h2 className="ld-show-h">{c.show1Title}</h2>
          <div className="ld-show-grid">
            <BrowserMock lang={lang} />
            <aside className="ld-show-aside">
              <p className="ld-aside-lead">
                <span>{c.show1AsideA}</span> {c.show1AsideB}
              </p>
              <div className="ld-feats">
                <span className="ld-feats-label">{c.featuresLabel}</span>
                {c.show1Features.map((f) => (
                  <span key={f}>{f}</span>
                ))}
              </div>
            </aside>
          </div>
        </section>

        {/* Showcase 02 — the publish flow, terminal mockup right. */}
        <section className="ld-section ld-show" id="publish">
          <span className="ld-idx">02</span>
          <h2 className="ld-show-h">{c.show2Title}</h2>
          <div className="ld-show-grid rev">
            <aside className="ld-show-aside">
              <p className="ld-aside-lead">
                <span>{c.show2AsideA}</span> {c.show2AsideB}
              </p>
              <div className="ld-feats">
                <span className="ld-feats-label">{c.featuresLabel}</span>
                {c.show2Features.map((f) => (
                  <span key={f}>{f}</span>
                ))}
              </div>
            </aside>
            <TerminalMock lang={lang} />
          </div>
        </section>

        <section className="ld-section" id="why">
          <span className="ld-idx">03</span>
          <h2>{c.whyTitle}</h2>
          <p className="ld-sub">{c.whySub}</p>
          <div className="ld-timeline">
            {c.timeline.map((tl) => (
              <div className="ld-tl" key={tl.b}>
                <b>{tl.b}</b>
                <span>{tl.s}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Bento + the 1px feature grid. */}
        <section className="ld-section" id="inside">
          <span className="ld-idx">04</span>
          <h2>{c.insideTitle}</h2>
          <p className="ld-sub">{c.insideSub}</p>
          <div className="ld-bento">
            <div className="ld-bcard ld-bcard-flow">
              <FlowMedia lang={lang} />
              <h3>{c.bentoFlowTitle}</h3>
              <p>{c.bentoFlowText}</p>
            </div>
            <div className="ld-bcol">
              <PassportBentoCard lang={lang} />
              <LcaBentoCard lang={lang} />
            </div>
          </div>
          <div className="ld-grid">
            {c.features.map((f) => (
              <div className="ld-feature" key={f.h}>
                <MiniIcon name={f.i} />
                <h3>{f.h}</h3>
                <p>{f.p}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="ld-section" id="diff">
          <span className="ld-idx">05</span>
          <h2>{c.diffTitle}</h2>
          <p className="ld-sub">{c.diffSub}</p>
          <div className="ld-grid ld-grid-4">
            {c.diffs.map((f) => (
              <div className="ld-feature" key={f.h}>
                <MiniIcon name={f.i} />
                <h3>{f.h}</h3>
                <p>{f.p}</p>
              </div>
            ))}
            <Link className="ld-feature ld-feature-cta" to="/login">
              <h3>{c.diffCtaTitle}</h3>
              <p>{c.diffCtaText}</p>
              <span className="ld-feature-cta-link">{c.ctaLogin} →</span>
            </Link>
          </div>
        </section>

        <BlogSection lang={lang} title={c.blogTitle} sub={c.blogSub} all={c.blogAll} />

        <section className="ld-cta">
          <h2>{c.ctaTitle}</h2>
          <p>{c.ctaText}</p>
          <Link className="ld-btn ld-btn-primary" to="/login">
            {c.ctaBtn}
          </Link>
        </section>

        <footer className="ld-footer ld-footer-lg">
          <div className="ld-foot-grid">
            <div className="ld-foot-brand-col">
              <span className="ld-footer-brand">
                <BrandMark size={18} className="brand-mark" />
                IDPP<b style={{ color: "var(--ld-accent)" }}>.net</b>
              </span>
              <p className="ld-foot-about">{c.footerAbout}</p>
            </div>
            <div className="ld-foot-col">
              <h4>{c.footerGeneralTitle}</h4>
              <ul>
                {c.footerGeneral.map((l) =>
                  isSpaRoute(l.href) ? (
                    <li key={l.href}>
                      <Link to={l.href}>{l.label}</Link>
                    </li>
                  ) : (
                    <li key={l.href}>
                      <a href={l.href}>{l.label}</a>
                    </li>
                  ),
                )}
              </ul>
            </div>
            <div className="ld-foot-col">
              <h4>{c.footerResourcesTitle}</h4>
              <ul>
                {c.footerResources.map((l) => (
                  <li key={l.href}>
                    <a href={l.href}>{l.label}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="ld-foot-col ld-foot-contact">
              <h4>{c.footerContactTitle}</h4>
              <address className="ld-foot-address">
                <strong>{COMPANY.legalName}</strong>
                <span className="ld-foot-address-label">{c.footerHq}</span>
                {COMPANY.hqLines.map((line) => (
                  <span key={line}>{line}</span>
                ))}
                <span>
                  {c.footerPhone}: <a href={`tel:${COMPANY.phoneE164}`}>{COMPANY.phone}</a>
                </span>
                <span>{c.footerTax}</span>
                <span>{c.footerMersis}</span>
              </address>
              <div className="ld-foot-certs">
                {COMPANY.certifications.map((cert) => (
                  <a key={cert.verifyUrl} href={cert.verifyUrl} target="_blank" rel="noopener noreferrer">
                    {cert.badges.map((b) => (
                      <img
                        key={b.src}
                        src={b.src}
                        alt={b.alt}
                        width={b.width}
                        height={b.height}
                        loading="lazy"
                      />
                    ))}
                  </a>
                ))}
              </div>
            </div>
          </div>
          <div className="ld-foot-bottom">
            <span>{c.footerRights}</span>
            <span>{c.footer}</span>
          </div>
        </footer>
      </div>
    </div>
  );
}
